import SlideFrame from "@/components/SlideFrame";
import {
  ShieldCheck, AlertTriangle, Lock, Network, Server, Quote, Building2, Award, Sparkles,
  Phone, Calendar, Mail, Check, X, ArrowRight, Briefcase, Scale, FileSearch, Megaphone,
  ClipboardList, GraduationCap, BookOpen, Users, AlertOctagon, TrendingUp, HelpCircle, Sliders
} from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";
import { submitContact } from "@/lib/api";

/* ============================ 10 · The Team ============================ */
export function S_Team({ idx, total }) {
  const compliance = [
    {
      n: "Jennifer Green, Esq.",
      r: "Head of Compliance",
      b: "Licensed attorney (CT, MA) and compliance professional in financial services and banking. Leads policy reviews, contract management, and regulatory compliance programs.",
      photo: "/brand/team/jennifer.jpg",
    },
    {
      n: "Bob Mullenbach",
      r: "Regulatory Compliance Expert",
      b: "25+ years in regulatory compliance for major financial institutions. Former SVP & Compliance Officer at Hatch Bank; ex-Managing Director, ProBank Austin.",
      photo: "https://customer-assets.emergentagent.com/job_complisun-pitch/artifacts/ospxunzk_Bob%20Mullenbach%204-17-24.jpeg",
    },
    {
      n: "Dorothy Jackson-Bush",
      r: "Licensing Specialist",
      b: "20+ years as a Senior Compliance Professional in risk-based testing, regulatory compliance, and internal controls. Certified Fraud Examiner; Master's in Project Management & Operations.",
      photo: "/brand/team/dorothy.jpg",
    },
    {
      n: "Rana Zouein",
      r: "Legal & Compliance Analyst",
      b: "5 years advising fintech, banking, and corporate clients across 12 jurisdictions. LL.M. Business Law, Beirut Bar Association — cross-border MENA + U.S. expertise.",
      photo: "/brand/team/rana.jpg",
    },
    {
      n: "Li-Chen Hung",
      r: "Legal & Compliance Analyst",
      b: "AML/BSA program implementation, audit coordination, and regulatory response. LL.M. Banking & Financial Law — Boston University.",
      photo: "/brand/team/li-chen.jpg",
    },
  ];
  const ops = [
    {
      n: "Jamie Tann",
      r: "Chief Operating Officer",
      b: "Operations and organizational strategy in fast-paced fintech environments. Streamlines complex workflows and builds high-performing teams.",
      photo: "/brand/team/jamie.jpg",
    },
    {
      n: "Senga Rutebuka",
      r: "Chief Growth Officer",
      b: "Drives partner expansion across fintech, lending, and banking ecosystems.",
      photo: "https://customer-assets.emergentagent.com/job_complisun-pitch/artifacts/80h5xlmv_Senga.jpg",
    },
    {
      n: "Rajesh Damarapati, CFA",
      r: "Fractional CTO · AI Architecture",
      b: "20 years in financial services and enterprise tech, specializing in AI-powered automation and decision platforms. Bloomberg Equity & Fixed Income certified.",
      photo: "/brand/team/rajesh.jpg",
    },
  ];

  const initials = (name) =>
    name.replace(/,.*$/, "").split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

  const Card = ({ p, testid }) => (
    <div className="border border-ink-900/12 rounded-md p-5 bg-cream-50 flex gap-4" data-testid={testid}>
      {p.photo ? (
        <div className="h-14 w-14 rounded-full overflow-hidden flex-shrink-0 border border-ink-900/10 bg-cream-100">
          <img
            src={p.photo}
            alt={p.n}
            loading="lazy"
            style={{
              objectPosition: p.objectPosition || "center top",
              transform: p.scale
                ? `translate(${p.translate || "0% 0%"}) scale(${p.scale})`
                : undefined,
              transformOrigin: "center",
            }}
            className="h-full w-full object-cover headshot"
          />
        </div>
      ) : (
        <div className="h-14 w-14 rounded-full flex-shrink-0 grid place-items-center serif text-base text-sun-700 bg-sun-500/15 border border-sun-500/25">
          {initials(p.n)}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="serif text-base text-ink-900 leading-tight">{p.n}</div>
        <div className="mono text-[10px] uppercase tracking-[0.18em] text-sun-700 mt-0.5 mb-2">{p.r}</div>
        <div className="text-xs text-ink-900/65 leading-relaxed">{p.b}</div>
      </div>
    </div>
  );

  return (
    <SlideFrame idx={idx} total={total} kicker="The Team" dark={false}>
      <p className="mono text-[11px] uppercase tracking-[0.22em] text-sun-700 mb-5">09 · the team</p>
      <h2 className="serif font-light text-5xl md:text-6xl leading-[1.05] tracking-tight max-w-4xl">
        The people behind <em className="italic text-sun-600">CompliSun</em>.
      </h2>
      <p className="mt-4 text-ink-900/65 text-base md:text-lg max-w-3xl">
        A deliberately structured fractional team — so you get senior expertise without the senior price tag,
        and analysts who can do the day-to-day work right alongside.
      </p>

      {/* Leadership / Guide row */}
      <div className="mt-8 border border-ink-900/12 rounded-md p-6 bg-white/70" data-testid="team-colin">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 items-center">
          <div className="md:col-span-1 flex md:justify-center">
            <img
              src="https://customer-assets.emergentagent.com/job_complisun-pitch/artifacts/v9nyy5uz_colin.png"
              alt="Colin Darke"
              className="h-16 w-16 rounded-full object-cover border border-ink-900/15 headshot"
              data-testid="team-colin-photo"
            />
          </div>
          <div className="md:col-span-3">
            <div className="mono text-[10px] uppercase tracking-[0.22em] text-sun-700 mb-1">CEO · Legal</div>
            <div className="serif text-3xl text-ink-900 leading-tight">Colin Darke</div>
            <ul className="mt-2 text-xs text-ink-900/60 space-y-0.5">
              <li>LL.M. Banking &amp; Financial Law, BU Law</li>
              <li>J.D., Michigan State Law</li>
            </ul>
          </div>
          <p className="md:col-span-8 text-ink-900/75 leading-relaxed">
            Attorney and CEO of CompliSun. Former <strong>Chief Compliance Officer and General Counsel</strong> at
            Rocket Loans and Associate General Counsel / VP — Mortgage &amp; Consumer Banking Legal at Flagstar Bank.
            Twenty-plus years advising financial institutions on regulatory matters.
          </p>
        </div>
      </div>

      {/* Fractional Compliance Team */}
      <div className="mt-6">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="h-4 w-4 text-sun-700" strokeWidth={1.5} />
          <div className="mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55">Fractional Compliance Team</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 stagger">
          {compliance.map((p) => (
            <Card key={p.n} p={p} testid={`team-compliance-${p.n.split(" ")[0].toLowerCase()}`} />
          ))}
        </div>
      </div>

      {/* Operations / Growth / Technology */}
      <div className="mt-5">
        <div className="flex items-center gap-2 mb-3">
          <Building2 className="h-4 w-4 text-sun-700" strokeWidth={1.5} />
          <div className="mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55">Operations · Growth · Technology</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {ops.map((p) => (
            <Card key={p.n} p={p} testid={`team-ops-${p.n.split(" ")[0].toLowerCase()}`} />
          ))}
        </div>
      </div>

      <p className="mt-5 mono text-[10px] uppercase tracking-[0.22em] text-ink-900/45">
        Fractional Legal Team led by Colin Darke · On-demand legal support available at Enterprise
      </p>
    </SlideFrame>
  );
}

/* ============================ 11 · Trusted By + Quotes ============================ */
